import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '../../../store';
import { AnyAction } from 'redux';
import { userInformation } from '../../../store/actions/DataAction';
import { getActiveSessions } from '../../../store/actions/SecurityActions';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import ApiLoader from '../../ApiLoader';
import Header from '../../shared/TailwindComponents/Header';
import FilterBarWithTabs, { TabItem } from '../../shared/TailwindComponents/FilterBarWithTabs';
import PersonalTab from './tabs/PersonalTab';
import PasswordTab from './tabs/PasswordTab';
import DeviceManagementTab from './tabs/DeviceManagementTab';
import BillingTab from './tabs/BillingTab';
import DeleteAccountTab from './tabs/DeleteAccountTab';
import PlansPage from '../../shared/Licenses/PlansPage';
import { getUserID, getUserType } from '../../../store/actions/ChatAction';
import { PiUserLight, PiLockLight, PiDeviceMobileLight, PiTrashLight, PiSealCheckLight, PiCreditCardLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import AuditLogPage from '../../AuditLog/AuditLogPage';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, accountSettingsTourSteps } from '../../shared/FeatureTour';

export interface UserProps {
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  StreetAddress: string;
  Email: string;
  CountryOfJurisdiction?: string;
  ZipCode?: string;
  ProfilePicture?: string;
  City?: string;
  Country?: string;
  State?: string;
  DateOfBirth?: string;
  Password?: string;
  Biography?: string;
  HourlyRate?: string;
  Designation?: string;
  CompletedCases?: number;
  RatingsAverage?: number;
  RatingsCount?: number;
  Bio?: string;
  DisplayLawfirmName?: string;
  CompanyRole?: string;
  LicenseDocumentLink?: string;
  FixedWorkingHours?: string;
  ExperienceYears?: number;
  Specializations?: string[];
  LanguagesSpoken?: string[];
  Education?: string;
  Certifications?: { title: string; fileUrl: string }[];
  Department?: string;
  JoinDate?: string;
  Role?: string;
  Awards?: { title: string; fileUrl: string }[];
  Publications?: { title: string; fileUrl: string }[];
  ProfessionalMemberships?: string[];
  Portfolio?: { title: string; fileUrl: string }[];
  AvailabilityStatus?: string;
  is_marketplace_active?: boolean;
  TermsOfServiceURL?: string;
  PreferredCaseTypes?: string[];
  PreferredBudgetMin?: number;
  PreferredBudgetMax?: number;
  PreferredLocations?: string[];
  PreferredKeywords?: string[];
}

const AccountSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, AnyAction>>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const userCred = localStorage.getItem('token') || sessionStorage.getItem('token');
  let userId = getUserID();
  let userType = getUserType();
  if (userCred) {
    const parsedData = JSON.parse(userCred);
    userId = parsedData.userId;
    userType = parsedData.userType;
  }

  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'personal';
  const [activeTab, setActiveTab] = useState(initialTab);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'account-settings',
    steps: accountSettingsTourSteps,
    readyToShow: !isLoading,
    autoStartDelay: 600,
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      // await Promise.all([
      //   dispatch(userInformation()),
      //   dispatch(getActiveSessions()),
      // ]);
      setIsLoading(false);
    };
    fetchData();
  }, [dispatch, userId, userType]);

  useEffect(() => {
    history.push(`?tab=${activeTab}`);
  }, [activeTab, history]);

  const isEU = window.location.hostname.includes('eu.pons.io');

  const tabs: TabItem[] = [
    { label: t('Personal'), value: 'personal', icon: <PiUserLight className="h-4 w-4" /> },
    { label: t('Password'), value: 'password', icon: <PiLockLight className="h-4 w-4" /> },
    // { label: t('Device Management'), value: 'device-management', icon: <PiDeviceMobileLight className="h-4 w-4" /> },
    ...(!isEU ? [{ label: t('Plans & Billing'), value: 'plans', icon: <PiCreditCardLight className="h-4 w-4" /> }] : []),
    { label: t('Activity'), value: 'activity', icon: <PiClockCounterClockwiseLight className="h-4 w-4" /> },
    // { label: t('Delete Account'), value: 'delete-account', icon: <PiTrashLight className="h-4 w-4" /> },
  ];

  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalTab />;
      case 'password':
        return <PasswordTab />;
      // case 'device-management':
      //   return <DeviceManagementTab />;
      case 'plans':
        return <PlansPage />;
      case 'activity':
        return <AuditLogPage embedded userOnly />;
      // case 'delete-account':
      //   return <DeleteAccountTab />;
      default:
        return <PersonalTab />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center flex-1 min-h-screen">
        <ApiLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark transition-colors duration-300">
      <Header />
      <div data-tour-id="account-tabs">
        <FilterBarWithTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabClick={handleTabChange}
        />
      </div>
      <div data-tour-id="account-content" className="flex-1 flex flex-col overflow-hidden">{renderTabContent()}</div>
      <FeatureTour
        steps={accountSettingsTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default AccountSettings;
